/*
 * PlayerEditModal — owner-only edit dialog for a single event player.
 *
 * Opens from the Players page when the owner clicks a player card.
 * Renders a small modal with: display name, email, phone, handicap,
 * team. Saves via PATCH /event-players/:id. The team picker uses the
 * full team name (or "Unassigned").
 *
 * Captures keyboard intent (Escape cancels, Enter saves) and locks
 * focus to the dialog while open.
 */

import { useEffect, useRef, useState } from 'react';
import { X, Save, Trash2, Link2, Check, MessageSquare, UserMinus } from 'lucide-react';

import { api } from '@/lib/api';
import { getInviteStatus, inviteStatusLabel } from '@/lib/inviteStatus';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { ConfirmModal } from '@/components/ConfirmModal';
import type { EventPlayer, TournamentTeam } from '@golf-app/shared';

interface Props {
  player: EventPlayer;
  teams: TournamentTeam[];
  /** Event OWNER (not captain). Gates the Unclaim action — detaching an
   *  account from a slot is owner-only per Travis's spec. */
  isOwner?: boolean;
  onClose: () => void;
  /** Fires after a successful save so the parent can refetch. */
  onSaved: () => void;
}

export function PlayerEditModal({ player, teams, isOwner = false, onClose, onSaved }: Props) {
  const [displayName, setDisplayName] = useState(player.displayName ?? '');
  const [email, setEmail] = useState(player.email ?? '');
  const [phone, setPhone] = useState(player.phone ?? '');
  // Handicap is a string for editing; parsed at submit time. Plus
  // handicaps come in as negative numbers (-2 etc) but the UI lets the
  // user type "+2" too.
  const [handicap, setHandicap] = useState(
    player.eventHandicapIndex != null ? formatHcpForInput(player.eventHandicapIndex) : '',
  );
  const [teamId, setTeamId] = useState<string | null>(player.teamId ?? null);
  // Role toggle. 'captain' grants owner-equivalent edit access to the
  // event (per the roles spec). Captains must be on a team. Anything
  // other than 'captain' is treated as 'player' for the UI — we don't
  // surface the legacy 'scorekeeper' role here.
  const [roleType, setRoleType] = useState<'player' | 'captain'>(
    player.roleType === 'captain' ? 'captain' : 'player',
  );

  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  // Personal invite link — fetched lazily on first use so opening the
  // modal doesn't mint claim tokens for players nobody is inviting.
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Two-step destructive confirm: open the type-to-confirm modal first.
  // The actual DELETE fires only after the user types DELETE exactly.
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  // Owner-only unclaim (wrong-person claim fix). Confirm-then-fire.
  const [confirmingUnclaim, setConfirmingUnclaim] = useState(false);
  const [unclaiming, setUnclaiming] = useState(false);
  // Show for claimed, non-owner rows only. The backend enforces the same
  // rules; this just keeps the button out of impossible states.
  const canUnclaim = isOwner && !!player.userId && player.roleType !== 'owner';

  async function performUnclaim() {
    setError(null);
    setUnclaiming(true);
    try {
      await api.unclaimEventPlayer(player.id);
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to unclaim the spot.');
      setUnclaiming(false);
      setConfirmingUnclaim(false);
    }
  }
  const [error, setError] = useState<string | null>(null);
  const firstFocus = useRef<HTMLInputElement | null>(null);

  async function fetchInviteLink(): Promise<string | null> {
    if (inviteUrl) return inviteUrl;
    setError(null);
    setLinkBusy(true);
    try {
      const { url } = await api.getPlayerClaimLink(player.id);
      setInviteUrl(url);
      return url;
    } catch (e: any) {
      setError(e?.message ?? 'Failed to get the invite link.');
      return null;
    } finally {
      setLinkBusy(false);
    }
  }

  async function copyInviteLink() {
    const url = await fetchInviteLink();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard API can be blocked (http, permissions) — fall back to a
      // prompt the user can copy from manually.
      window.prompt('Copy this invite link:', url);
    }
  }

  async function textInviteLink() {
    const url = await fetchInviteLink();
    if (!url) return;
    const body = `You're in for the golf trip! Claim your spot on 18Gimmies: ${url}`;
    // sms: opens Messages with the player's number (when we have one) and
    // the invite prefilled. The ?& body form works on both iOS and Android.
    window.location.href = `sms:${(phone || player.phone || '').trim()}?&body=${encodeURIComponent(body)}`;
  }

  async function performRemove() {
    setError(null);
    setRemoving(true);
    try {
      await api.removeTournamentPlayer(player.id);
      // Modal closes via parent onSaved → refetch → editing state nulled.
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to remove player.');
      setRemoving(false);
      setConfirmingRemove(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => firstFocus.current?.focus(), 30);
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !saving) onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', handleKey); };
  }, [saving, onClose]);

  async function save() {
    setError(null);
    if (displayName.trim().length < 1) { setError('Name is required.'); return; }

    // Catch malformed emails client-side. Backend rejects too, but the
    // server error surfaces as a generic red banner; this is friendlier.
    const emailValidate = email.trim();
    if (emailValidate.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValidate)) {
      setError('Please enter a valid email address.');
      return;
    }

    // Parse handicap. Accept "+2", "+2.5", "-1", "0", "12", "12.5", ".8",
    // "0.8", or empty. The leading-decimal form (".8") is common — golfers
    // type the value the way they read it on a scorecard, and the old
    // regex insisted on a leading digit.
    // One decimal place only — clamped on save so 12.55 becomes 12.6.
    let hcp: number | null = null;
    const raw = handicap.trim();
    if (raw.length > 0) {
      // Accept either "12", "12.5", ".5" — sign optional. We split the
      // numeric body into "digits + optional decimal" OR "decimal + digits"
      // so leading-decimal inputs pass the same validation path.
      if (!/^[+-]?(\d+(\.\d+)?|\.\d+)$/.test(raw)) {
        setError('Handicap must be a number (e.g. 12, 12.5, +2.5, .8).');
        return;
      }
      const cleaned = raw.replace(/^\+/, '-');   // "+2" → "-2" (plus handicap)
      const n = parseFloat(cleaned);
      if (!Number.isFinite(n)) { setError('Handicap must be a number (or blank).'); return; }
      if (n < -10 || n > 54) { setError('Handicap must be between +10 and 54.'); return; }
      // Snap to one decimal so storage stays clean.
      hcp = Math.round(n * 10) / 10;
    }

    // Confirm before any contact-info mutation that would either change
    // a saved value, clear a saved value, or — on an unclaimed player —
    // add contact for the first time (because the backend's auto-invite
    // fires an invite email the moment a new email lands on an unclaimed
    // slot, and the captain should know that).
    const emailTrimmed   = email.trim();
    const phoneTrimmed   = phone.trim();
    const savedEmail     = (player.email ?? '').trim();
    const savedPhone     = (player.phone ?? '').trim();
    const emailIsClearing = !!savedEmail && !emailTrimmed;
    const emailIsChanging = !!savedEmail && !!emailTrimmed && emailTrimmed.toLowerCase() !== savedEmail.toLowerCase();
    const emailIsAdding   = !savedEmail && !!emailTrimmed;
    const phoneIsClearing = !!savedPhone && !phoneTrimmed;
    const phoneIsChanging = !!savedPhone && !!phoneTrimmed && phoneTrimmed !== savedPhone;
    const phoneIsAdding   = !savedPhone && !!phoneTrimmed;

    // Will the backend send an invite as a side effect of this save?
    // Backend rule: auto-invite fires when the player is unclaimed
    // (no user_id) AND the email or phone value is new vs. what was on
    // the row. We mirror that check client-side so the confirm text
    // accurately tells the captain what will happen.
    const playerUnclaimed = !player.userId;
    const willSendEmail   = playerUnclaimed && (emailIsAdding || emailIsChanging);
    const willSendSms     = playerUnclaimed && (phoneIsAdding || phoneIsChanging); // logged-only until Twilio

    if (emailIsClearing || emailIsChanging || emailIsAdding ||
        phoneIsClearing || phoneIsChanging || phoneIsAdding) {
      const lines: string[] = [];
      if (emailIsClearing) lines.push(`Remove email "${savedEmail}"?`);
      if (emailIsChanging) lines.push(`Change email from "${savedEmail}" to "${emailTrimmed}"?`);
      if (emailIsAdding)   lines.push(`Add email "${emailTrimmed}"?`);
      if (phoneIsClearing) lines.push(`Remove phone "${savedPhone}"?`);
      if (phoneIsChanging) lines.push(`Change phone from "${savedPhone}" to "${phoneTrimmed}"?`);
      if (phoneIsAdding)   lines.push(`Add phone "${phoneTrimmed}"?`);
      lines.push('');
      if (willSendEmail) {
        lines.push(`An invite email will be sent to "${emailTrimmed}" so they can claim this slot. Any previous invite link for this player stops working.`);
      } else if (willSendSms) {
        lines.push(`SMS invites are coming soon. Your captain will be able to resend manually once the Twilio number is live.`);
      } else {
        lines.push('Heads up: this is the contact we use to send invites for this player.');
      }
      const ok = window.confirm(lines.join('\n'));
      if (!ok) return;
    }

    // Captain must be on a team. The role toggle is hidden when there's
    // no team, but a power user could still flip it; guard at submit.
    if (roleType === 'captain' && !teamId) {
      setError('Captains must be assigned to a team. Pick a team first.');
      return;
    }

    setSaving(true);
    try {
      // The backend's PLAYER patch accepts every field independently;
      // we send everything since the form covers the editable surface.
      // Team change is handled by the same call. Role change wiring
      // (team's captain pointer) happens server-side atomically.
      await api.updateTournamentPlayer(player.id, {
        displayName: displayName.trim(),
        // Send null when the input is blank so the backend clears the
        // value. The PATCH route now honors null on email/phone (was a
        // COALESCE bug that silently preserved the old value).
        email: emailTrimmed ? emailTrimmed : null,
        phone: phoneTrimmed ? phoneTrimmed : null,
        eventHandicapIndex: hcp,
        teamId,
        roleType,
      });
      onSaved();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save player.');
      setSaving(false);
    }
  }

  return (
    <div
      role="presentation"
      onClick={() => { if (!saving) onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.66)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="player-edit-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && !saving) {
            e.preventDefault();
            save();
          }
        }}
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: 22,
          maxWidth: 460,
          width: '100%',
          // Cap the modal at the DYNAMIC viewport height and scroll
          // internally. iOS 'vh' is the large viewport (Safari toolbar
          // hidden), so 90vh could exceed the actually-visible area and
          // clip the header + Save row with no way to scroll to them.
          // dvh tracks the real visible height as the toolbar shows/hides.
          maxHeight: 'calc(100dvh - 40px)',
          overflowY: 'auto',
          boxShadow: '0 24px 50px rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PlayerAvatar avatarUrl={player.avatarUrl} displayName={displayName || player.displayName} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 id="player-edit-title" style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'var(--text)', letterSpacing: '-0.01em' }}>
              Edit player
            </h2>
            <div style={{ fontSize: 12, color: 'var(--text-mut)', marginTop: 2 }}>
              {inviteStatusLabel(getInviteStatus(player))}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--text-mut)', cursor: 'pointer',
              // 36x36 so the close button clears the touch-target
              // floor on phones. Was 26x26 (padding: 4) which made
              // dismissing the modal on iPhone feel finicky.
              width: 36, height: 36,
              borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X width={18} height={18} />
          </button>
        </div>

        {error && <div className="t-error" style={{ marginBottom: 0 }}>{error}</div>}

        {/* Fields */}
        <div className="t-field">
          <label htmlFor="pe-name" className="t-label">Name</label>
          <input
            id="pe-name"
            ref={firstFocus}
            className="t-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={60}
            autoComplete="name"
          />
        </div>

        <div className="t-field-row">
          <div className="t-field">
            <label htmlFor="pe-hcp" className="t-label">Handicap</label>
            <input
              id="pe-hcp"
              className="t-input"
              value={handicap}
              onChange={(e) => setHandicap(e.target.value)}
              placeholder="e.g. 12.5 or +2"
              inputMode="decimal"
              maxLength={6}
            />
          </div>
          <div className="t-field">
            <label htmlFor="pe-team" className="t-label">Team</label>
            <select
              id="pe-team"
              className="t-input"
              value={teamId ?? ''}
              onChange={(e) => setTeamId(e.target.value || null)}
              style={{ appearance: 'auto', paddingRight: 10 }}
            >
              <option value="">Unassigned</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Role toggle. Only shown when the player is on a team — a
            captain without a team has no team_id to wire the captain
            pointer to. When unassigned the row collapses to a hint. */}
        <div className="t-field">
          <div className="t-label">Role</div>
          {teamId ? (
            <div className="t-chip-row">
              <button
                type="button"
                className={`t-chip ${roleType === 'player' ? 't-chip-active' : ''}`}
                onClick={() => setRoleType('player')}
              >
                Player
              </button>
              <button
                type="button"
                className={`t-chip ${roleType === 'captain' ? 't-chip-active' : ''}`}
                onClick={() => setRoleType('captain')}
              >
                Captain
              </button>
            </div>
          ) : (
            <div className="t-hint">
              Captains must be on a team. Assign a team above to enable the captain role.
            </div>
          )}
          {roleType === 'captain' && teamId && (
            <div className="t-hint" style={{ marginTop: 6 }}>
              Captains get owner-equivalent edit access (everything except deleting or archiving the event). If a different captain is currently leading this team, they'll be demoted to player.
            </div>
          )}
        </div>

        <div className="t-field">
          <label htmlFor="pe-email" className="t-label">Email</label>
          <input
            id="pe-email"
            className="t-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={120}
            inputMode="email"
            autoComplete="email"
            placeholder="optional"
          />
        </div>

        <div className="t-field">
          <label htmlFor="pe-phone" className="t-label">Phone</label>
          <input
            id="pe-phone"
            className="t-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={30}
            inputMode="tel"
            autoComplete="tel"
            placeholder="optional"
          />
        </div>

        {/* Unclaim — owner-only escape hatch for wrong-person claims.
            Detaches the linked account; the slot keeps its name,
            handicap, team, and matches and goes back to guest. */}
        {canUnclaim && (
          <div className="t-field">
            <div className="t-label">Claimed spot</div>
            <button
              type="button"
              className="t-btn t-btn-secondary"
              onClick={() => setConfirmingUnclaim(true)}
              disabled={saving || removing || unclaiming}
              style={{ minHeight: 40, alignSelf: 'flex-start' }}
            >
              <UserMinus width={14} height={14} /> {unclaiming ? 'Unclaiming…' : 'Unclaim spot'}
            </button>
            <div className="t-hint" style={{ marginTop: 6 }}>
              Disconnects the account that claimed this spot. The player keeps their
              name, handicap, team, and matches, and the spot can be claimed again.
            </div>
          </div>
        )}

        {/* Personal invite link — only for unclaimed slots. Anyone with
            the link can claim this roster spot, so it's surfaced here
            (owner/captain-only modal) rather than on the public card.
            Fills the SMS gap: copy it or jump straight into Messages. */}
        {!player.userId && (
          <div className="t-field">
            <div className="t-label">Personal invite link</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="t-btn t-btn-secondary"
                onClick={copyInviteLink}
                disabled={linkBusy || saving || removing}
                style={{ minHeight: 40 }}
              >
                {copied
                  ? <><Check width={14} height={14} /> Copied</>
                  : <><Link2 width={14} height={14} /> {linkBusy ? 'Getting link…' : 'Copy invite link'}</>}
              </button>
              <button
                type="button"
                className="t-btn t-btn-ghost"
                onClick={textInviteLink}
                disabled={linkBusy || saving || removing}
                style={{ minHeight: 40 }}
              >
                <MessageSquare width={14} height={14} /> Text it
              </button>
            </div>
            <div className="t-hint" style={{ marginTop: 6 }}>
              One-tap link for {displayName.trim() || player.displayName || 'this player'} to claim
              this spot. Anyone with the link can claim it, so send it straight to them.
            </div>
          </div>
        )}

        {/* Action row. Remove is left-anchored as a destructive ghost
            button so it's available but doesn't compete for attention
            with Save. Cancel + Save sit on the right.
            flexWrap lets the row break onto two lines on narrow phones
            so Cancel + Save never get pushed off-screen; the 1fr spacer
            is also flexShrink so it collapses cleanly on the wrap. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setConfirmingRemove(true)}
            disabled={saving || removing}
            title="Remove this player from the event"
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              color: '#ef4444',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              minHeight: 40,
              cursor: (saving || removing) ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Trash2 width={14} height={14} />
            {removing ? 'Removing…' : 'Remove'}
          </button>
          <div style={{ flex: 1, minWidth: 0 }} />
          <button
            type="button"
            className="t-btn t-btn-ghost"
            onClick={onClose}
            disabled={saving || removing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="t-btn t-btn-primary"
            onClick={save}
            disabled={saving || removing}
            style={{ minWidth: 110 }}
          >
            {saving ? 'Saving…' : <><Save width={14} height={14} /> Save</>}
          </button>
        </div>
      </div>

      {/* Type-to-confirm dialog. The user must type DELETE (exactly) to
          enable the red Remove button. Mirrors the pattern used to
          delete an event so the UX feels consistent for destructive
          actions across the app. */}
      {/* Unclaim confirm. No type-to-confirm — it's reversible (the spot
          can be claimed again), so a plain confirm is enough friction. */}
      <ConfirmModal
        open={confirmingUnclaim}
        title={`Unclaim ${player.displayName || 'this spot'}?`}
        body={(
          <p>
            The account that claimed this spot will be disconnected from the event.
            {' '}{player.displayName || 'The player'} stays on the roster with their
            handicap, team, and matches, ready to be claimed by the right person.
          </p>
        )}
        confirmLabel="Unclaim spot"
        tone="danger"
        busy={unclaiming}
        onCancel={() => { if (!unclaiming) setConfirmingUnclaim(false); }}
        onConfirm={performUnclaim}
      />

      <ConfirmModal
        open={confirmingRemove}
        title={`Remove ${player.displayName || 'player'} from this event?`}
        body={(
          <p>
            They'll be removed from the active roster. Any scores already
            recorded stay in match history. You can re-add them later.
          </p>
        )}
        requireType="DELETE"
        confirmLabel="Remove player"
        tone="danger"
        busy={removing}
        onCancel={() => { if (!removing) setConfirmingRemove(false); }}
        onConfirm={performRemove}
      />
    </div>
  );
}

/** Stored value (-2, -2.5, 12.5, etc.) → input string ("+2", "+2.5", "12.5").
 *  Shows one decimal only when the value has a fractional part. */
function formatHcpForInput(n: number): string {
  const abs = Math.abs(n);
  const str = Number.isInteger(abs) ? String(abs) : (Math.round(abs * 10) / 10).toFixed(1);
  return n < 0 ? `+${str}` : str;
}
